import React, { useMemo, useState } from "react";

import { faAngleDown, faLock } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Avatar, AvatarGroup, ButtonIcon, Icon, Spinner, Text } from "@performyard/py-components";

import ExpandableText from "./ExpandableText";

import { useMeetingNotes } from "../../../../api/hooks";
import { usePastMeetingNotesData } from "../hooks";

import { toAvatarGroupEmployees } from "../../utils/participants";
import { formatPastMeetingDateTime, getParticipantName, resolveDefaultNotesParticipantId } from "../utils";

import { config } from "../config";
import { useMeetingContext } from "../context";

type PastMeetingItemProps = {
	meetingId: string;
	meetingName: string;
	startDateTime: Date;
	endDateTime: Date;
	participants: Array<{ userId: string; name?: string }>;
	isExpanded: boolean;
	onToggle: () => void;
	userId: string;
};

/**
 * Individual past meeting item with expandable notes section.
 * Uses AvatarGroup for N-participant note switching.
 * Current user sees shared + private notes; other participants see shared only.
 */
const PastMeetingItem = ({
	meetingId,
	meetingName,
	startDateTime,
	endDateTime,
	participants,
	isExpanded,
	onToggle,
	userId,
}: PastMeetingItemProps): React.ReactElement => {
	const { participantNames, workspace } = useMeetingContext();

	const notesMeetingId = isExpanded ? meetingId : "";
	const { data: notes, isLoading: isNotesLoading } = useMeetingNotes(notesMeetingId);
	const notesData = usePastMeetingNotesData({ notes, userId, participants });

	const avatarEmployees = useMemo(
		() =>
			toAvatarGroupEmployees(participants, {
				currentUserId: userId,
				resolveName: (id) => getParticipantName(workspace, participantNames, id),
			}),
		[participants, userId, workspace, participantNames]
	);

	const [selectedParticipantId, setSelectedParticipantId] = useState(() =>
		resolveDefaultNotesParticipantId({
			userId,
			meetingCreatorId: userId,
			isAdminView: false,
			participants,
			workspace,
			participantNames,
		})
	);

	const isViewingOwnNotes = selectedParticipantId === userId;

	const selectedName = useMemo(() => {
		if (isViewingOwnNotes) return "My Shared Notes";
		const name = getParticipantName(workspace, participantNames, selectedParticipantId);
		return `${name || "Participant"}'s Notes`;
	}, [selectedParticipantId, isViewingOwnNotes, workspace, participantNames]);

	const selectedAvatarName = useMemo(() => {
		const employee = avatarEmployees.find((e) => e._id === selectedParticipantId);
		return employee?.name || "Participant";
	}, [avatarEmployees, selectedParticipantId]);

	// Get content for the selected participant
	const sharedNoteContent = isViewingOwnNotes
		? (notesData.primaryPublicNotes?.content ?? "")
		: (notesData.participantNotesMap.get(selectedParticipantId)?.content ?? "");

	const privateNoteContent = isViewingOwnNotes ? (notesData.userPrivateNotes?.content ?? "") : "";

	return (
		<div className="border-b border-solid border-neutral-2">
			<div
				className="flex h-12 cursor-pointer select-none items-center gap-2 bg-white px-4 py-2 hover:bg-neutral-1"
				onClick={onToggle}
			>
				<div className="flex min-w-0 flex-1 flex-col items-start justify-center">
					<div className="w-full py-line-clamp-1">
						<Text size="paragraph-3" weight="semi-bold">
							{meetingName}
						</Text>
					</div>
					<Text size="caption" weight="regular" color="neutral-9">
						{formatPastMeetingDateTime(startDateTime, endDateTime)}
					</Text>
				</div>
				<div className={isExpanded ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"}>
					<ButtonIcon icon={faAngleDown} aria-label="Toggle past meeting details" />
				</div>
			</div>

			{isExpanded && (
				<div className="flex flex-col gap-2 border-t border-solid border-neutral-2 bg-white p-4">
					{isNotesLoading ? (
						<div className="flex items-center justify-center py-4">
							<Spinner size={config.spinnerSize.small} />
						</div>
					) : (
						<div className="flex flex-col items-start gap-2">
							<div className="flex w-full items-center justify-between">
								<div className="flex items-center gap-1">
									<Avatar
										size={config.layout.dropdownAvatarSize}
										id={selectedParticipantId}
										name={selectedAvatarName}
									/>
									<Text size="paragraph-3" weight="semi-bold">
										{selectedName}
									</Text>
								</div>
								<AvatarGroup
									employees={avatarEmployees}
									maxLength={4}
									size={config.layout.dropdownAvatarSize}
									employeeNameTooltip={true}
									onAvatarClick={(employee) => setSelectedParticipantId(employee._id)}
									overflowBehavior="dropdown"
									onOverflowSelect={(employee) => setSelectedParticipantId(employee._id)}
									selectedEmployeeId={selectedParticipantId}
								/>
							</div>

							<ExpandableText
								key={`shared-${selectedParticipantId}`}
								content={sharedNoteContent}
								collapseHeight={config.pastMeetingNotesCollapseHeight}
								emptyMessage="No shared notes recorded"
								className="w-full"
							/>

							{isViewingOwnNotes && privateNoteContent && (
								<>
									<div className="flex items-center gap-1 pt-2">
										<Icon icon={faLock} size={12} color="neutral-5" />
										<Text size="paragraph-3" weight="semi-bold">
											My Private Notes
										</Text>
									</div>
									<ExpandableText
										key="private-notes"
										content={privateNoteContent}
										collapseHeight={config.pastMeetingNotesCollapseHeight}
										emptyMessage=""
										className="w-full"
									/>
								</>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default PastMeetingItem;
